import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IEnvironment } from '@atlas-workspace/shared/environments';
import {
  DocumentModel,
  DownloadStatus,
  ETablePagination,
  IFile,
  IPackDocument,
  IPackShowDocument,
} from '@atlas-workspace/shared/models';
import { PaginationUtil } from '@atlas-workspace/shared/service';
import { environment } from '@environment-client';
import { plainToClass } from 'class-transformer';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { delay, expand, filter, finalize, map, mergeMap, tap } from 'rxjs/operators';

@Injectable()
export class DocumentsService {
  private totalDocuments$ = new BehaviorSubject<number | null>(null);
  private downloadLoading$ = new BehaviorSubject<boolean>(false);
  private downloadPackDelay = 2000;
  constructor(private http: HttpClient, @Inject('ENVIRONMENT') private env: IEnvironment) {}

  public get totalDocuments(): Observable<number | null> {
    return this.totalDocuments$.asObservable();
  }

  public getDocuments(
    projectId: number,
    unitId: number,
    sort: string,
    folderId: number | null = null,
    perPage = 50,
    pageNumber = 1,
    search = ''
  ): Observable<DocumentModel[]> {
    let params = new HttpParams();
    params = params.set('unit_id', unitId.toString());
    params = params.append('per_page', perPage.toString());
    params = params.append('page', pageNumber.toString());
    params = params.append('only_index', true);
    if (sort) {
      params = params.append('sort_by', sort);
    }
    if (folderId) {
      params = params.append('folder_id', folderId.toString());
    }
    if (search) {
      params = params.append('search', search.toString());
    }
    return this.http
      .get<any>(`${environment.apiBaseUrl}api/v1/client/projects/${projectId}/documents`, {
        params,
        observe: 'response',
      })
      .pipe(
        tap((result) => {
          const totalCount = PaginationUtil.convertPaginationType(result.headers, ETablePagination.TotalCount);
          this.totalDocuments$.next(totalCount);
        }),
        map((res) => res.body.data.documents),
        map((data: IFile[]) => plainToClass(DocumentModel, data))
      );
  }

  showDocument(projectId: number, id: number, unitId: number): Observable<DocumentModel> {
    let params = new HttpParams();
    params = params.set('unit_id', unitId.toString());
    return this.http
      .get<any>(`${environment.apiBaseUrl}api/v1/client/projects/${projectId}/documents/${id}`, {
        params: params,
      })
      .pipe(
        map((res) => res.data),
        map((data: IFile) => plainToClass(DocumentModel, data))
      );
  }

  public createDownloadPack(projectId: number, body: FormData, forFdv = false): Observable<any> {
    this.downloadLoading$.next(true);
    return this.http
      .post<{ data: IPackDocument }>(
        `${this.env.apiBaseUrl}api/v1/projects/${projectId}/document_download_packs`,
        body,
        {
          params: {
            for_fdv: forFdv,
          },
        }
      )
      .pipe(
        map((res) => res.data),
        delay(this.downloadPackDelay),
        mergeMap((pack: IPackDocument) => this.showDownloadPack(projectId, pack.id)),
        expand((showPack: IPackShowDocument) => {
          if (showPack.status !== DownloadStatus.Done) {
            return of(null).pipe(
              delay(this.downloadPackDelay),
              mergeMap(() => this.showDownloadPack(projectId, showPack.id))
            );
          }

          return EMPTY;
        }),
        filter((showPack: IPackShowDocument) => showPack.status === DownloadStatus.Done),
        finalize(() => this.downloadLoading$.next(false)),
        tap((showPack: IPackShowDocument) => {
          window.open(showPack.filename.download_url, '_self');
        })
      );
  }

  public showDownloadPack(projectId: number, id: number): Observable<IPackShowDocument> {
    return this.http
      .get<{ data: IPackShowDocument }>(
        `${this.env.apiBaseUrl}api/v1/projects/${projectId}/document_download_packs/${id}`
      )
      .pipe(map((res) => res.data));
  }

  public downloadFromS3(url: string, fileName: string): Observable<void> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((blob: Blob) => {
        const fileURL = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = fileURL;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(fileURL);
      })
    );
  }
}
